import { useCallback, useId, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import type { ClubCrest, CrestPiece } from "@legacy-manager/engine";
import { useT } from "../../i18n";
import { EscudoDoClube } from "./Escudo";
import { TAMANHO_MAXIMO, TAMANHO_MINIMO, type EstiloDeFigura } from "./catalogo";
import { SeletorDeFigura } from "./SeletorDeFigura";
import "./criador.css";

/**
 * O editor do escudo: forma, padrão, cores e até quatro peças soltas.
 *
 * **O escudo inteiro é um valor só, e quem chama é dono dele.** O criador não
 * guarda escudo nenhum: recebe um `ClubCrest` e devolve outro a cada mexida. A
 * tela que monta o clube já precisa do escudo junto do nome, da sigla e do
 * uniforme para gravar tudo numa vez, e um estado interno aqui dentro seria uma
 * segunda cópia da mesma coisa, pronta para discordar da primeira.
 *
 * **Só a seleção mora aqui.** Qual peça está escolhida é assunto de quem edita
 * agora, não do escudo gravado: sair da tela e voltar não precisa lembrar disso,
 * e o escudo salvo não tem onde guardar.
 *
 * **A prévia não é enfeite.** O escudo só se escolhe na criação, e o mesmo
 * desenho aparece com 20 pixels numa tabela de vinte linhas. Ver os três
 * tamanhos enquanto se desenha é o que impede alguém de confirmar uma mancha e
 * descobrir na primeira rodada.
 */

/*
 * A grade do escudo, repetida do `Escudo.tsx`.
 *
 * O arquivo vizinho não exporta as duas medidas, e exportá-las para uso daqui
 * abriria a porta para o resto do jogo tratar o `viewBox` como coisa ajustável.
 * Ele não é: 40 por 44 é o mesmo do `Crest` dos átomos e do escudo cosmético, e
 * é o que faz o escudo criado entrar numa tabela sem desalinhar a linha.
 */
const LARGURA = 40;
const ALTURA = 44;

/** O limite de peças, que é de legibilidade e não de bytes. */
const MAXIMO_DE_PECAS = 4;

/*
 * O piso e o teto de tamanho vêm do catálogo, e não daqui.
 *
 * O número é o mesmo problema visto de dois lugares: `size` é porcentagem da
 * largura do escudo, no chip de tabela o escudo tem 20 pixels, e uma peça de 24
 * ocupa ali menos de cinco. Abaixo disso a silhueta some no antisserrilhado e
 * sobra um ponto colorido. Como o escudo não se edita depois, esse pontinho
 * ficaria para sempre.
 *
 * Um segundo piso escrito aqui seria um número para discordar do outro no dia em
 * que alguém mexesse num só, e o editor é justamente quem precisa cobrar o piso
 * do catálogo na barra de tamanho.
 */

/**
 * Quanto o centro de uma peça para antes da borda da caixa do escudo.
 *
 * As quatro formas fecham para dentro perto dos cantos e a flâmula ainda tem o
 * corte no meio da base, então centro colado na borda põe a peça quase toda fora
 * do recorte. Doze é o que mantém o centro dentro do desenho nas quatro formas,
 * na horizontal e na vertical. Canto com canto ainda escapa do círculo, e é para
 * esse resto que a prévia serve.
 */
const MARGEM_DO_CENTRO = 12;

/**
 * Quanto uma seta do teclado move a peça.
 *
 * Dois por cento são 0,8 unidade do escudo, uns quatro pixels no palco de
 * trabalho: dá para ver que andou e ainda dá para encostar a peça onde se quer.
 */
const PASSO_DO_TECLADO = 2;

/**
 * O lado do palco em pixels, que também é o que esconde a sigla.
 *
 * O `EscudoDoClube` só desenha a sigla abaixo de 64 pixels. Trabalhar acima
 * desse corte é de propósito: três letras no meio do palco tapariam justamente a
 * peça que está sendo posicionada. A sigla continua visível nas três prévias, que
 * é onde ela importa.
 */
const LADO_DE_TRABALHO = 220;

/**
 * Quanto cada peça nova se afasta da posição que o catálogo escolheu.
 *
 * A primeira cai onde o catálogo manda, que já é o lugar de emblema e foge da
 * ponta de baixo da flâmula e do losango. As seguintes precisam desviar: quatro
 * peças no mesmo centro parecem uma só, e quem acabou de escolher a segunda
 * figura acha que o clique não pegou. Desviada, a peça nova chega visível e o
 * arrasto vira ajuste, não descoberta.
 */
const DESVIOS_INICIAIS = [
  { x: 0, y: 0 },
  { x: -17, y: -8 },
  { x: 17, y: -8 },
  { x: 0, y: 22 },
] as const;

/**
 * Os três tamanhos que o escudo terá de verdade, e o nome de cada lugar.
 *
 * Não são amostras bonitas: 20 é o chip de tabela, 40 é o painel de jogo e o
 * cartão da próxima partida, que no jogo variam de 34 a 42, e 54 é o cabeçalho
 * do clube. Quem confirma aqui já viu o escudo em todos os lugares onde vai
 * encontrá-lo depois.
 *
 * O rótulo do meio é `partida`, e não `grande`: a chave `grande` é a da prévia
 * da camisa, onde ela nomeia um tamanho de conferência que não existe em lugar
 * nenhum da tela. Aqui os três são lugares, e o do meio ainda é menor que o
 * vizinho da direita, então chamá-lo de grande seria descrever errado o que a
 * pessoa está vendo.
 *
 * O palco de trabalho fica de fora da fila porque não é lugar nenhum do jogo: é
 * onde se desenha, e nenhum jogador verá o escudo daquele tamanho.
 */
const PREVIAS = [
  { tamanho: 20, rotulo: "criador.previa.tabela" },
  { tamanho: 40, rotulo: "criador.previa.partida" },
  { tamanho: 54, rotulo: "criador.previa.cabecalho" },
] as const;

const FORMAS = ["shield", "circle", "diamond", "pennant"] as const;
const PADROES = ["solid", "sash", "stripes", "halves"] as const;

/** Setas do teclado como deslocamento, para não virar quatro `if` iguais. */
const SETAS: Record<string, readonly [number, number]> = {
  ArrowLeft: [-PASSO_DO_TECLADO, 0],
  ArrowRight: [PASSO_DO_TECLADO, 0],
  ArrowUp: [0, -PASSO_DO_TECLADO],
  ArrowDown: [0, PASSO_DO_TECLADO],
};

function limitar(valor: number, minimo: number, maximo: number): number {
  return Math.min(maximo, Math.max(minimo, valor));
}

/**
 * Uma casa decimal na posição.
 *
 * Inteiro daria passos de 1% da largura, uns dois pixels no palco, e o arrasto
 * ficaria engasgado sob o dedo. Mais de uma casa só engorda o save com ruído que
 * ninguém enxerga nem no cabeçalho do clube.
 */
function arredondar(valor: number): number {
  return Math.round(valor * 10) / 10;
}

export function CriadorDeEscudo({
  escudo,
  sigla,
  aoMudar,
}: {
  escudo: ClubCrest;
  /** A sigla do clube, que aparece nas prévias pequenas como aparecerá no jogo. */
  sigla: string;
  aoMudar: (escudo: ClubCrest) => void;
}): ReactNode {
  const t = useT();
  const identificador = useId();
  const [selecionada, setSelecionada] = useState<number | null>(null);
  const [escolhendoFigura, setEscolhendoFigura] = useState(false);
  /*
   * A aba do catálogo é estado desta tela, e não do seletor.
   *
   * Fechar o catálogo para colocar a peça e reabrir na aba de contornos porque
   * era lá que se estava é o que se espera de quem está montando um escudo de
   * quatro peças do mesmo traço. Guardada dentro do seletor, a escolha morreria
   * junto com ele a cada peça.
   */
  const [estiloDoCatalogo, setEstiloDoCatalogo] = useState<EstiloDeFigura>("cheios");

  const camada = useRef<SVGSVGElement>(null);
  const arrastando = useRef<number | null>(null);
  /*
   * A distância entre o dedo e o centro da peça no instante em que o arrasto
   * começou. Sem ela a peça salta para debaixo do dedo no primeiro toque, e quem
   * queria ajustar dois pixels perde a posição que já tinha.
   */
  const pega = useRef({ x: 0, y: 0 });

  const peca = selecionada === null ? undefined : escudo.pieces[selecionada];
  const cheio = escudo.pieces.length >= MAXIMO_DE_PECAS;

  const trocarPeca = useCallback(
    (indice: number, mudanca: Partial<CrestPiece>): void => {
      aoMudar({
        ...escudo,
        pieces: escudo.pieces.map((atual, i) => (i === indice ? { ...atual, ...mudanca } : atual)),
      });
    },
    [escudo, aoMudar],
  );

  const mover = useCallback(
    (indice: number, x: number, y: number): void => {
      trocarPeca(indice, {
        x: arredondar(limitar(x, MARGEM_DO_CENTRO, 100 - MARGEM_DO_CENTRO)),
        y: arredondar(limitar(y, MARGEM_DO_CENTRO, 100 - MARGEM_DO_CENTRO)),
      });
    },
    [trocarPeca],
  );

  /**
   * O ponto do dedo ou do mouse em porcentagem do escudo.
   *
   * A conta sai da matriz do próprio SVG, e não de `getBoundingClientRect`. O
   * escudo é 40 por 44 dentro de uma caixa quadrada, então sobra tarja nos dois
   * lados, e a caixa ainda encolhe no celular. Pedir a matriz resolve a tarja, a
   * escala e qualquer zoom da página de uma vez, sem repetir aqui uma conta que o
   * navegador já fez.
   */
  function pontoNoEscudo(evento: PointerEvent<SVGElement>): { x: number; y: number } | null {
    const svg = camada.current;
    if (!svg) return null;
    const matriz = svg.getScreenCTM();
    if (!matriz) return null;
    const ponto = new DOMPoint(evento.clientX, evento.clientY).matrixTransform(matriz.inverse());
    return { x: (ponto.x / LARGURA) * 100, y: (ponto.y / ALTURA) * 100 };
  }

  /*
   * Um caminho só para dedo e mouse.
   *
   * Eventos de ponteiro já unificam os dois, e a captura faz o arrasto continuar
   * mesmo quando a peça escapa de baixo do dedo, que numa peça de quatro pixels e
   * meio é o caso comum. O `touch-action: none` que impede a página de rolar
   * junto está no CSS, que é onde o navegador olha antes do primeiro evento.
   */
  function comecarArrasto(evento: PointerEvent<SVGRectElement>, indice: number): void {
    setSelecionada(indice);
    const ponto = pontoNoEscudo(evento);
    const atual = escudo.pieces[indice];
    if (!ponto || !atual) return;
    pega.current = { x: atual.x - ponto.x, y: atual.y - ponto.y };
    arrastando.current = indice;
    evento.currentTarget.setPointerCapture(evento.pointerId);
  }

  function seguirArrasto(evento: PointerEvent<SVGRectElement>): void {
    const indice = arrastando.current;
    if (indice === null) return;
    const ponto = pontoNoEscudo(evento);
    if (!ponto) return;
    mover(indice, ponto.x + pega.current.x, ponto.y + pega.current.y);
  }

  function largarArrasto(evento: PointerEvent<SVGRectElement>): void {
    arrastando.current = null;
    if (evento.currentTarget.hasPointerCapture(evento.pointerId)) {
      evento.currentTarget.releasePointerCapture(evento.pointerId);
    }
  }

  /** Setas movem a peça em foco, que é o arrasto de quem não usa ponteiro. */
  function teclado(evento: KeyboardEvent<SVGRectElement>, indice: number): void {
    const passo = SETAS[evento.key];
    if (!passo) return;
    const atual = escudo.pieces[indice];
    if (!atual) return;
    // Sem isto a seta rola a página inteira em vez de mover a peça.
    evento.preventDefault();
    mover(indice, atual.x + passo[0], atual.y + passo[1]);
  }

  /*
   * O seletor entrega a peça pronta, com desenho, grade e contorno dentro. O que
   * falta é o que só este escudo sabe: para onde ela vai e de que cor nasce.
   *
   * Escolher uma figura fecha o catálogo. A peça aparece no palco já selecionada,
   * e com o catálogo aberto por cima ela ficaria escondida atrás de uma lista de
   * mil e duzentas figuras.
   */
  function adicionarFigura(base: CrestPiece): void {
    if (cheio) return;
    const desvio = DESVIOS_INICIAIS[escudo.pieces.length] ?? { x: 0, y: 0 };
    const nova: CrestPiece = {
      ...base,
      x: limitar(base.x + desvio.x, MARGEM_DO_CENTRO, 100 - MARGEM_DO_CENTRO),
      y: limitar(base.y + desvio.y, MARGEM_DO_CENTRO, 100 - MARGEM_DO_CENTRO),
      // O catálogo nasce branco porque não conhece as cores deste escudo. O
      // contorno já foi escolhido para contrastar com o fundo, então é o único
      // palpite que aparece em escudo claro e em escudo escuro.
      color: escudo.detail,
    };
    aoMudar({ ...escudo, pieces: [...escudo.pieces, nova] });
    setSelecionada(escudo.pieces.length);
    setEscolhendoFigura(false);
  }

  /*
   * Empilhar é trocar de lugar com a vizinha, porque quem desenha o escudo é a
   * ordem da lista: a última da lista fica por cima. A seleção acompanha a peça,
   * e não a posição, senão mandar para frente passaria a seleção para outra peça.
   */
  function empilhar(indice: number, direcao: 1 | -1): void {
    const destino = indice + direcao;
    const daqui = escudo.pieces[indice];
    const dali = escudo.pieces[destino];
    if (!daqui || !dali) return;
    const pecas = escudo.pieces.slice();
    pecas[indice] = dali;
    pecas[destino] = daqui;
    aoMudar({ ...escudo, pieces: pecas });
    setSelecionada(destino);
  }

  function remover(indice: number): void {
    aoMudar({ ...escudo, pieces: escudo.pieces.filter((_, i) => i !== indice) });
    setSelecionada(null);
  }

  return (
    <section className="criador">
      <h3 className="label-muted">{t("criador.escudo")}</h3>

      {/*
        Desenho de um lado, controles do outro, quando a tela dá para os dois.

        Numa coluna só, que é o que cabe no celular, mexer na cor empurra o
        escudo para fora da tela: quem está escolhendo a cor deixa de ver o que
        a cor faz. No computador sobra largura para os dois ficarem à vista, e
        o palco ainda gruda no topo enquanto a coluna dos controles rola.
      */}
      <div className="criador-corpo">
        <div className="criador-lado">
        <div className="criador-palco">
          <EscudoDoClube crest={escudo} short={sigla} size={LADO_DE_TRABALHO} />

          <svg
            ref={camada}
            className="criador-camada"
            width={LADO_DE_TRABALHO}
            height={LADO_DE_TRABALHO}
            viewBox={`0 0 ${LARGURA} ${ALTURA}`}
          >
            {/* Clicar no vazio solta a seleção, que é como se sai do modo de mexer
                numa peça sem precisar de um botão para isso. */}
            <rect
              x="0"
              y="0"
              width={LARGURA}
              height={ALTURA}
              fill="transparent"
              onPointerDown={() => setSelecionada(null)}
            />

            {escudo.pieces.map((atual, indice) => {
              const lado = (atual.size / 100) * LARGURA;
              const centroX = (atual.x / 100) * LARGURA;
              const centroY = (atual.y / 100) * ALTURA;
              const canto = { x: centroX - lado / 2, y: centroY - lado / 2 };
              const escolhida = indice === selecionada;

              return (
                <g key={indice} transform={`rotate(${atual.rotation} ${centroX} ${centroY})`}>
                  {escolhida && (
                    /*
                      Duas molduras, uma escura contínua e uma clara tracejada por
                      cima. Uma cor só some no escudo que tiver essa cor, e o fundo
                      é escolhido por quem desenha: não dá para apostar em nenhuma.
                    */
                    <>
                      <rect
                        className="criador-moldura-base"
                        x={canto.x}
                        y={canto.y}
                        width={lado}
                        height={lado}
                      />
                      <rect
                        className="criador-moldura"
                        x={canto.x}
                        y={canto.y}
                        width={lado}
                        height={lado}
                      />
                    </>
                  )}
                  <rect
                    className="criador-alca"
                    x={canto.x}
                    y={canto.y}
                    width={lado}
                    height={lado}
                    fill="transparent"
                    role="button"
                    aria-pressed={escolhida}
                    aria-label={t("criador.peca.contagem", {
                      total: indice + 1,
                      maximo: MAXIMO_DE_PECAS,
                    })}
                    tabIndex={0}
                    onFocus={() => setSelecionada(indice)}
                    onPointerDown={(evento) => comecarArrasto(evento, indice)}
                    onPointerMove={seguirArrasto}
                    onPointerUp={largarArrasto}
                    onPointerCancel={largarArrasto}
                    onKeyDown={(evento) => teclado(evento, indice)}
                  />
                </g>
              );
            })}
          </svg>
        </div>

        <div className="criador-previa">
          <h4 className="label-muted">{t("criador.previa.titulo")}</h4>
          <div className="criador-previa-fila">
            {PREVIAS.map(({ tamanho, rotulo }) => (
              <div key={tamanho} className="criador-previa-item">
                {/* As três alinham pela base, como alinham na linha da tabela e na
                    barra do cabeçalho. */}
                <div className="criador-previa-caixa">
                  <EscudoDoClube crest={escudo} short={sigla} size={tamanho} />
                </div>
                <span className="label-muted corta">{t(rotulo)}</span>
              </div>
            ))}
          </div>
        </div>
        </div>

        <div className="criador-coluna">
        <div className="criador-bloco">
          <h4 className="label-muted">{t("criador.forma")}</h4>
          <div className="criador-opcoes">
            {FORMAS.map((forma) => (
              <button
                key={forma}
                type="button"
                className="criador-opcao corta"
                aria-pressed={escudo.shape === forma}
                onClick={() => aoMudar({ ...escudo, shape: forma })}
              >
                {t(`criador.formas.${forma}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="criador-bloco">
          <h4 className="label-muted">{t("criador.padrao")}</h4>
          <div className="criador-opcoes">
            {PADROES.map((padrao) => (
              <button
                key={padrao}
                type="button"
                className="criador-opcao corta"
                aria-pressed={escudo.pattern === padrao}
                onClick={() => aoMudar({ ...escudo, pattern: padrao })}
              >
                {t(`criador.padroes.${padrao}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="criador-bloco">
          <h4 className="label-muted">{t("criador.cores.titulo")}</h4>
          <div className="criador-cores">
            <label className="criador-cor">
              <span className="label-muted corta">{t("criador.cores.fundo")}</span>
              <input
                type="color"
                value={escudo.primary}
                onChange={(evento) => aoMudar({ ...escudo, primary: evento.target.value })}
              />
            </label>
            <label className="criador-cor">
              <span className="label-muted corta">{t("criador.cores.segunda")}</span>
              {/* No padrão liso a segunda cor não desenha nada. Desligar o controle
                  diz isso sem precisar de frase nenhuma. */}
              <input
                type="color"
                value={escudo.secondary}
                disabled={escudo.pattern === "solid"}
                onChange={(evento) => aoMudar({ ...escudo, secondary: evento.target.value })}
              />
            </label>
            <label className="criador-cor">
              <span className="label-muted corta">{t("criador.cores.contorno")}</span>
              <input
                type="color"
                value={escudo.detail}
                onChange={(evento) => aoMudar({ ...escudo, detail: evento.target.value })}
              />
            </label>
          </div>
        </div>

        <div className="criador-bloco">
          <div className="criador-cabecalho">
            <h4 className="label-muted corta">{t("criador.peca.titulo")}</h4>
            <span className="label-muted fixo">
              {t("criador.peca.contagem", { total: escudo.pieces.length, maximo: MAXIMO_DE_PECAS })}
            </span>
            <button
              type="button"
              className="fixo"
              disabled={cheio}
              aria-expanded={escolhendoFigura}
              onClick={() => setEscolhendoFigura((aberto) => !aberto)}
            >
              {t("criador.peca.adicionar")}
            </button>
          </div>

          {/*
            Cheio, a tela não escreve frase nenhuma sobre o limite. A contagem
            acima já mostra "4 de 4" e o botão de adicionar já está desligado: uma
            linha dizendo que o máximo é quatro repetiria em prosa o que dois
            controles ao lado dela dizem como dado, e texto que explica a tela é
            justamente o que não entra aqui.
          */}

          {/* Cheio, o catálogo fecha sozinho: escolher lá dentro não acrescentaria
              peça nenhuma, e uma lista que não responde é pior que lista fechada. */}
          {escolhendoFigura && !cheio && (
            <SeletorDeFigura
              estilo={estiloDoCatalogo}
              aoTrocarDeEstilo={setEstiloDoCatalogo}
              aoEscolher={adicionarFigura}
            />
          )}

          {escudo.pieces.length === 0 && !escolhendoFigura && (
            <p className="criador-aviso label-muted">{t("criador.peca.vazio")}</p>
          )}

          {peca && selecionada !== null && (
            <div className="criador-controles">
              <div className="criador-faixa">
                <label className="label-muted corta" htmlFor={`${identificador}-girar`}>
                  {t("criador.peca.girar")}
                </label>
                {/*
                  Passo de cinco graus. Numa barra de dedo, um pixel de percurso já
                  vale uns dois graus, então passo fino não daria controle nenhum e
                  ainda deixaria o giro parando em número quebrado.
                */}
                <input
                  id={`${identificador}-girar`}
                  type="range"
                  min={0}
                  max={355}
                  step={5}
                  value={peca.rotation}
                  onChange={(evento) =>
                    trocarPeca(selecionada, { rotation: Number(evento.target.value) })
                  }
                />
              </div>

              <div className="criador-faixa">
                <label className="label-muted corta" htmlFor={`${identificador}-tamanho`}>
                  {t("criador.peca.tamanho")}
                </label>
                <input
                  id={`${identificador}-tamanho`}
                  type="range"
                  min={TAMANHO_MINIMO}
                  max={TAMANHO_MAXIMO}
                  value={peca.size}
                  onChange={(evento) =>
                    trocarPeca(selecionada, {
                      size: limitar(Number(evento.target.value), TAMANHO_MINIMO, TAMANHO_MAXIMO),
                    })
                  }
                />
              </div>

              <div className="criador-faixa">
                <label className="label-muted corta" htmlFor={`${identificador}-cor`}>
                  {t("criador.peca.cor")}
                </label>
                <input
                  id={`${identificador}-cor`}
                  type="color"
                  value={peca.color}
                  onChange={(evento) => trocarPeca(selecionada, { color: evento.target.value })}
                />
              </div>

              <div className="criador-acoes">
                <button
                  type="button"
                  className="criador-opcao corta"
                  disabled={selecionada >= escudo.pieces.length - 1}
                  onClick={() => empilhar(selecionada, 1)}
                >
                  {t("criador.peca.paraFrente")}
                </button>
                <button
                  type="button"
                  className="criador-opcao corta"
                  disabled={selecionada <= 0}
                  onClick={() => empilhar(selecionada, -1)}
                >
                  {t("criador.peca.paraTras")}
                </button>
                <button
                  type="button"
                  className="criador-opcao corta"
                  onClick={() => remover(selecionada)}
                >
                  {t("criador.peca.remover")}
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </section>
  );
}
